/**
 * Guest meeting URL builder and expiry helpers for RI/RA visio.
 */

import { getGuestMeetingToken } from '@/services/remote/sessions.service';
import { getErrorMessage } from '@/utils/errors-utils';

/** Guest visio URL plus token expiry. */
export type GuestMeetingTarget = {
  url: string;
  /** Epoch ms; `0` when the API sent an unparseable date (treated as expired). */
  expiresAt: number;
};

/** Cached {@link GuestMeetingTarget} keyed by session. */
export type CachedGuestMeetingTarget = GuestMeetingTarget & { sessionId: string };

/** Popup handle opened in the click stack, or `blocked` when `window.open` fails. */
export type MeetingPlaceholder =
  | { kind: 'popup'; assign: (url: string) => void; close: () => void }
  | { kind: 'blocked' };

/** Mobile guest-meeting URL (`/guest-meeting/:id?guestToken=`). */
function buildGuestMeetingUrl(baseUrl: string, sessionId: string, guestToken: string): string {
  return `${baseUrl}/guest-meeting/${sessionId}?guestToken=${encodeURIComponent(guestToken)}`;
}

/** True when the cached target matches `sessionId` and has not expired. */
export function isGuestTargetFresh(
  target: CachedGuestMeetingTarget | null,
  sessionId: string,
  now = Date.now(),
): target is CachedGuestMeetingTarget {
  return !!target && target.sessionId === sessionId && target.expiresAt > now;
}

/**
 * Safari popup-blocker workaround: open `about:blank` synchronously in the click
 * stack, then redirect once the guest token is available. Safari only grants
 * `window.open` inside the user gesture, so it must not sit behind an `await`.
 *
 * `noopener` is deliberately omitted: with it, `window.open` returns `null` per
 * spec even on success, which makes the blocked case undetectable — and we need
 * the handle to redirect the tab.
 */
export function openMeetingPlaceholderWindow(): MeetingPlaceholder {
  if (typeof window === 'undefined' || typeof window.open !== 'function') {
    return { kind: 'blocked' };
  }

  const popup = window.open('about:blank', '_blank');
  if (!popup || popup.closed) {
    return { kind: 'blocked' };
  }

  return {
    kind: 'popup',
    assign(url: string) {
      if (popup.closed) return;
      try {
        popup.location.replace(url);
      } catch {
        popup.location.href = url;
      }
    },
    close() {
      if (!popup.closed) popup.close();
    },
  };
}

/** Opens an already known meeting URL. Must stay in the click stack (see above). */
export function openMeetingWindow(url: string): boolean {
  if (typeof window === 'undefined' || typeof window.open !== 'function') {
    return false;
  }
  const popup = window.open(url, '_blank');
  return !!popup && !popup.closed;
}

/** Last resort when the popup blocker cannot be worked around. */
export function navigateToMeetingSameTab(url: string): void {
  if (typeof window !== 'undefined') {
    window.location.href = url;
  }
}

/** Mobile web base URL (no trailing slash). Null if `VITE_MOBILE_APP_URL` is unset. */
function getMobileAppBaseUrl(): string | null {
  const fromEnv = (import.meta.env.VITE_MOBILE_APP_URL as string | undefined)
    ?.trim()
    .replace(/\/$/, '');
  return fromEnv || null;
}

/** Tutor post-activation redirect URL — null if VITE_MOBILE_APP_URL is not configured. */
export function getTutorMobileLoginUrl(): string | null {
  const base = getMobileAppBaseUrl();
  return base ? `${base}/login` : null;
}

/**
 * Guest JWT for an RI/RA (or other remote) meeting, resolved to the mobile Visio
 * URL (`/guest-meeting/:id?guestToken=…`) — full room (chat, whiteboard), no
 * Firebase login.
 */
export async function fetchGuestMeetingTarget(sessionId: string): Promise<GuestMeetingTarget> {
  const baseUrl = getMobileAppBaseUrl();
  if (!baseUrl) throw new Error(getErrorMessage('mobileRedirect'));
  const { guestToken, expiresAt } = await getGuestMeetingToken(sessionId);
  const parsedExpiry = Date.parse(expiresAt);
  return {
    url: buildGuestMeetingUrl(baseUrl, sessionId, guestToken),
    expiresAt: Number.isFinite(parsedExpiry) ? parsedExpiry : 0,
  };
}
